import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './index.scss';

import AntAPI from '../../utils/AntAPI';
import AntLocalization from '../../utils/AntLocalization';

export default function AntDeploymentRoadmap(props) {
    var { session, onNavigate } = props;

    var API = useMemo(() => new AntAPI('setting', 'SysOnboarding'), []);
    var navigate = useNavigate();

    var [loading, setLoading] = useState(true);
    var [data, setData] = useState(null);
    var [expandedPhase, setExpandedPhase] = useState(null);
    var [dismissed, setDismissed] = useState(false);

    var loadRoadmap = useCallback(async () => {
        setLoading(true);
        var result = await API.postActionWithData('getRoadmap', {});
        if (result && result.data) {
            setData(result.data);
            if (result.data.phases) {
                for (var i = 0; i < result.data.phases.length; i++) {
                    var phase = result.data.phases[i];
                    if (phase.completed < phase.total) {
                        setExpandedPhase(phase.code);
                        break;
                    }
                }
            }
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        loadRoadmap();
    }, []);

    var handleDismiss = useCallback(async () => {
        await API.postActionWithData('dismissRoadmap', {});
        setDismissed(true);
    }, []);

    var handleItemClick = useCallback(
        (item) => {
            if (item.blocked || !item.path) return;
            if (onNavigate) {
                onNavigate(item.path);
            } else {
                navigate(item.path);
            }
        },
        [onNavigate, navigate]
    );

    var handlePhaseToggle = useCallback(
        (phaseCode) => {
            setExpandedPhase((prev) => (prev === phaseCode ? null : phaseCode));
        },
        []
    );

    // ─── Don't render ─────────────────────────────────────────
    if (dismissed) return null;
    if (loading) {
        return (
            <div className="deployment-roadmap deployment-roadmap--loading">
                <div className="deployment-roadmap__skeleton" />
            </div>
        );
    }
    if (!data || !data.phases || data.phases.length === 0) return null;
    if (data.allDone) return null;

    var progressPct = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;

    return (
        <div className="deployment-roadmap">
            {/* Header */}
            <div className="deployment-roadmap__header">
                <div className="deployment-roadmap__header-left">
                    <div className="deployment-roadmap__icon-wrap">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                            <path
                                d="M10 2L3 6v8l7 4 7-4V6l-7-4z"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinejoin="round"
                                fill="none"
                            />
                            <path d="M10 10v8M3 6l7 4 7-4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="deployment-roadmap__title">
                            {AntLocalization.translateText({
                                vi: 'Thiết lập doanh nghiệp',
                                en: 'Business Setup',
                                fr: 'Configuration',
                                es: 'Configuración',
                                id: 'Pengaturan',
                                ja: 'セットアップ',
                                zh: '企业设置',
                                ko: '설정',
                                hi: 'सेटअप',
                                ar: 'الإعداد'
                            })}
                        </h3>
                        <p className="deployment-roadmap__subtitle">
                            {data.completed}/{data.total}{' '}
                            {AntLocalization.translateText({
                                vi: 'đã hoàn thành',
                                en: 'completed',
                                fr: 'terminé',
                                es: 'completado',
                                id: 'selesai',
                                ja: '完了',
                                zh: '已完成',
                                ko: '완료',
                                hi: 'पूर्ण',
                                ar: 'مكتمل'
                            })}
                        </p>
                    </div>
                </div>
                <div className="deployment-roadmap__header-right">
                    <div className="deployment-roadmap__progress-ring">
                        <svg viewBox="0 0 36 36" className="deployment-roadmap__ring-svg">
                            <path
                                className="deployment-roadmap__ring-bg"
                                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                            <path
                                className="deployment-roadmap__ring-fill"
                                strokeDasharray={progressPct + ', 100'}
                                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            />
                        </svg>
                        <span className="deployment-roadmap__ring-text">{progressPct}%</span>
                    </div>
                    <button className="deployment-roadmap__dismiss-btn" onClick={handleDismiss} title="Dismiss">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Phases */}
            <div className="deployment-roadmap__phases">
                {data.phases.map((phase) => {
                    var isExpanded = expandedPhase === phase.code;
                    var phaseDone = phase.completed >= phase.total;

                    return (
                        <div
                            key={phase.code}
                            className={
                                'deployment-roadmap__phase' +
                                (isExpanded ? ' deployment-roadmap__phase--expanded' : '') +
                                (phaseDone ? ' deployment-roadmap__phase--done' : '')
                            }
                        >
                            <button className="deployment-roadmap__phase-header" onClick={() => handlePhaseToggle(phase.code)}>
                                <div className="deployment-roadmap__phase-left">
                                    <span
                                        className={
                                            'deployment-roadmap__phase-badge' +
                                            (phaseDone ? ' deployment-roadmap__phase-badge--done' : '')
                                        }
                                    >
                                        {phaseDone ? (
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path
                                                    d="M2.5 6l2.5 2.5 5-5"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        ) : (
                                            phase.phase
                                        )}
                                    </span>
                                    <span className="deployment-roadmap__phase-title">
                                        {AntLocalization.translateText(phase.title)}
                                    </span>
                                    {phase.optional && (
                                        <span className="deployment-roadmap__optional-tag">
                                            {AntLocalization.translateText({
                                                vi: 'Tuỳ chọn',
                                                en: 'Optional',
                                                fr: 'Optionnel',
                                                es: 'Opcional',
                                                id: 'Opsional',
                                                ja: 'オプション',
                                                zh: '可选',
                                                ko: '선택',
                                                hi: 'वैकल्पिक',
                                                ar: 'اختياري'
                                            })}
                                        </span>
                                    )}
                                </div>
                                <div className="deployment-roadmap__phase-right">
                                    <span className="deployment-roadmap__phase-count">
                                        {phase.completed}/{phase.total}
                                    </span>
                                    <svg
                                        className={
                                            'deployment-roadmap__chevron' +
                                            (isExpanded ? ' deployment-roadmap__chevron--open' : '')
                                        }
                                        width="14"
                                        height="14"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                    >
                                        <path
                                            d="M4 6l4 4 4-4"
                                            stroke="currentColor"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>
                            </button>

                            {isExpanded && (
                                <div className="deployment-roadmap__items">
                                    {phase.desc && (
                                        <p className="deployment-roadmap__phase-desc">
                                            {AntLocalization.translateText(phase.desc)}
                                        </p>
                                    )}
                                    {phase.items.map((item) => (
                                        <button
                                            key={item.code}
                                            className={
                                                'deployment-roadmap__item' +
                                                (item.done ? ' deployment-roadmap__item--done' : '') +
                                                (item.blocked ? ' deployment-roadmap__item--blocked' : '') +
                                                (!item.done && !item.blocked && item.path
                                                    ? ' deployment-roadmap__item--actionable'
                                                    : '')
                                            }
                                            onClick={() => handleItemClick(item)}
                                            disabled={item.blocked || !item.path}
                                        >
                                            <span className="deployment-roadmap__item-check">
                                                {item.done ? (
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <circle cx="8" cy="8" r="7" fill="#2ecc71" />
                                                        <path
                                                            d="M5 8l2 2 4-4"
                                                            stroke="#fff"
                                                            strokeWidth="1.5"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                ) : item.blocked ? (
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <circle cx="8" cy="8" r="7" stroke="#ccc" strokeWidth="1.2" />
                                                        <circle cx="8" cy="8" r="2" fill="#ccc" />
                                                    </svg>
                                                ) : (
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <circle
                                                            cx="8"
                                                            cy="8"
                                                            r="7"
                                                            stroke="var(--AnteBaseColor1, #6e97f6)"
                                                            strokeWidth="1.2"
                                                        />
                                                    </svg>
                                                )}
                                            </span>
                                            <div className="deployment-roadmap__item-content">
                                                <span className="deployment-roadmap__item-title">
                                                    {AntLocalization.translateText(item.title)}
                                                </span>
                                                {item.desc && (
                                                    <span className="deployment-roadmap__item-desc">
                                                        {AntLocalization.translateText(item.desc)}
                                                    </span>
                                                )}
                                            </div>
                                            {!item.done && !item.blocked && item.path && (
                                                <svg
                                                    className="deployment-roadmap__item-arrow"
                                                    width="14"
                                                    height="14"
                                                    viewBox="0 0 14 14"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M5 3l4 4-4 4"
                                                        stroke="currentColor"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
